import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { QRCodeSVG } from "qrcode.react";
import { AlfredFiatPaymentInstructions, AlfredInitiateDepositTransaction, AlfredSessionSnapshot, alfredService } from "src/api";
import { formatNumberToLocale } from "src/utils";
import "./alfred-payment-instructions.scss";

interface Props {
  sessionId: string;
  transaction: AlfredInitiateDepositTransaction;
  /** Platform display precision for the received (stable) currency. */
  precision: number;
  /**
   * Net stable the user actually receives — `toAmount` minus the platform
   * fee, same value as the form's "Receive total". `transaction.toAmount`
   * is the gross Alfred conversion (fee not yet taken), so we show this
   * instead. `0`/undefined (e.g. after a reload with no live quote) falls
   * back to the gross amount.
   */
  netReceiveAmount?: number;
  onReset: () => void;
}

/** Field-name → display-label map. Anything not listed humanises the
 *  raw key, so a brand-new field on a new rail still renders. */
const LABELS: Record<string, string> = {
  paymentType: "Rail",
  bankName: "Bank",
  accountHolderName: "Beneficiary",
  accountNumber: "Account number",
  routingNumber: "Routing number",
  clabe: "CLABE",
  reference: "Reference",
  paymentDescription: "Description",
  pixKey: "PIX Key",
  // Alfred PIX returns the copia-e-cola string under `qrCode` (no
  // separate `pixCode` field on this rail). Both are payable strings
  // the user wants to copy, so we surface both as "PIX Key" rows.
  pixCode: "PIX Key",
  qrCode: "PIX Key",
  cbu: "CBU",
  cvu: "CVU",
  cci: "CCI",
  iban: "IBAN",
  swiftBic: "SWIFT/BIC",
  beneficiaryAddress: "Beneficiary address",
  expirationDate: "Expires at",
};

// Field key → ALFRED.INSTRUCTION.* suffix, so labels translate while the
// English value in LABELS stays the `defaultValue`. Keys not listed fall
// back to the humanised raw key.
const INSTRUCTION_LABEL_KEY: Record<string, string> = {
  paymentType: "RAIL",
  bankName: "BANK",
  accountHolderName: "BENEFICIARY",
  accountNumber: "ACCOUNT_NUMBER",
  routingNumber: "ROUTING_NUMBER",
  clabe: "CLABE",
  reference: "REFERENCE",
  paymentDescription: "DESCRIPTION",
  pixKey: "PIX_KEY",
  pixCode: "PIX_KEY",
  qrCode: "PIX_KEY",
  cbu: "CBU",
  cvu: "CVU",
  cci: "CCI",
  iban: "IBAN",
  swiftBic: "SWIFT_BIC",
  beneficiaryAddress: "BENEFICIARY_ADDRESS",
  expirationDate: "EXPIRES_AT",
};

const HEADER_KEYS = new Set(["paymentType", "expirationDate"]);
const QR_KEYS = new Set(["pixCode", "qrCode", "paymentLink", "deepLink"]);
// Keys that must NEVER show up as text rows — they're either a
// redundant link form (`paymentLink`, `deepLink`) or a base64 PNG
// blob that's useless as text (`qrCodeImage`). `qrCode` / `pixCode`
// stay visible: they're the copia-e-cola string the user pastes
// when they can't scan, and we relabel them "PIX Key" above.
const HIDDEN_KEYS = new Set(["paymentLink", "deepLink", "qrCodeImage"]);
const TERMINAL_STATUSES = new Set(["ON_CHAIN_COMPLETED", "FIAT_TRANSFER_COMPLETED", "FAILED"]);
const POLL_INTERVAL_MS = 4_000;

const humaniseKey = (key: string): string =>
  LABELS[key] ??
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

const formatValue = (value: unknown): string => {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/** Decimal-string → display-string at the EXACT precision Alfred sent (no
 *  rounding — crypto amounts must show every decimal). Only the integer part
 *  is grouped with thousands separators; the decimal part is kept verbatim. */
const formatAmount = (raw: string | undefined | null): string => {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (!/^-?\d*\.?\d+$/.test(s)) return s; // not a plain number → leave as-is
  const neg = s.startsWith("-");
  const [intPart, decPart] = s.replace("-", "").split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return (neg ? "-" : "") + grouped + (decPart != null ? `.${decPart}` : "");
};

/** mm:ss countdown to an ISO-8601 expiration. Drives the expiry modal. */
const useCountdown = (iso?: string): { remaining: string; expired: boolean } => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);
  return useMemo(() => {
    if (!iso) return { remaining: "—", expired: false };
    const target = Date.parse(iso);
    if (!Number.isFinite(target)) return { remaining: "—", expired: false };
    const diffMs = target - now;
    if (diffMs <= 0) return { remaining: "00:00", expired: true };
    const total = Math.floor(diffMs / 1000);
    const m = String(Math.floor(total / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return { remaining: `${m}:${s}`, expired: false };
  }, [iso, now]);
};

/**
 * Polls `GET /psp/alfred/session` for live transaction state. Stops
 * once the status reaches a terminal value or the component unmounts
 * — no point hitting the API forever after the deposit settled or
 * failed. Caller passes `enabled=false` to pause (e.g. once we've
 * already shown the expiry modal).
 */
const useAlfredSessionPolling = (sessionId: string, enabled: boolean): AlfredSessionSnapshot | null => {
  const [snapshot, setSnapshot] = useState<AlfredSessionSnapshot | null>(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    stoppedRef.current = false;
    if (!sessionId || !enabled) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await alfredService.getSession(sessionId);
        if (cancelled) return;
        if (data) setSnapshot(data);
        if (data?.transactionStatus && TERMINAL_STATUSES.has(data.transactionStatus)) {
          stoppedRef.current = true;
        }
      } catch (e) {
        // Transient errors don't stop polling — backend wakes up,
        // we resume on next tick.
        console.warn("Alfred session poll failed:", e);
      }
    };
    tick();
    const id = setInterval(() => {
      if (stoppedRef.current) return;
      tick();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sessionId, enabled]);

  return snapshot;
};

const Row: React.FC<{ label: string; value: string; copyable?: boolean }> = ({ label, value, copyable }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    if (!copyable) return;
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined
    );
  };
  return (
    <div className="alfred-instructions-row">
      <span className="alfred-instructions-row__label">{label}</span>
      <span
        className={`alfred-instructions-row__value ${copyable ? "alfred-instructions-row__value--copyable" : ""}`}
        onClick={onCopy}
        title={
          copyable
            ? copied
              ? t("ALFRED.INSTRUCTION.COPIED", { defaultValue: "Copied!" })
              : t("ALFRED.INSTRUCTION.COPY", { defaultValue: "Click to copy" })
            : undefined
        }
      >
        {value}
      </span>
    </div>
  );
};

const StatusBanner: React.FC<{ status: string | null }> = ({ status }) => {
  const { t } = useTranslation();
  if (!status) return null;
  const lower = status.toLowerCase();
  const isFailure = status === "FAILED";
  const isSuccess = status === "ON_CHAIN_COMPLETED" || status === "FIAT_TRANSFER_COMPLETED";
  const cls = isFailure
    ? "alfred-status-banner--error"
    : isSuccess
      ? "alfred-status-banner--success"
      : "alfred-status-banner--info";
  return (
    <div className={`alfred-status-banner ${cls}`}>
      {t(`DEPOSIT_FORM.TX_STATUS.${status}`, { defaultValue: humaniseKey(lower) })}
    </div>
  );
};

/**
 * Horizontal step chain visualising the canonical ONRAMP progression
 * (per Alfred docs):
 *   CREATED → FIAT_DEPOSIT_RECEIVED → TRADE_COMPLETED →
 *   ON_CHAIN_INITIATED → ON_CHAIN_COMPLETED
 *
 * Steps before the current one render filled, the current one
 * pulses, the rest stay outlined. On `FAILED` we mark the chain
 * red at the position the failure happened — but since the upstream
 * payload doesn't include "where it failed", we draw a single red
 * X after whatever the last completed step was.
 */
const TX_STEPS: { key: string; labelKey: string }[] = [
  { key: "CREATED", labelKey: "DEPOSIT_FORM.STEP.CREATED" },
  { key: "FIAT_DEPOSIT_RECEIVED", labelKey: "DEPOSIT_FORM.STEP.FIAT_DEPOSIT_RECEIVED" },
  { key: "TRADE_COMPLETED", labelKey: "DEPOSIT_FORM.STEP.TRADE_COMPLETED" },
  { key: "ON_CHAIN_INITIATED", labelKey: "DEPOSIT_FORM.STEP.ON_CHAIN_INITIATED" },
  { key: "ON_CHAIN_COMPLETED", labelKey: "DEPOSIT_FORM.STEP.ON_CHAIN_COMPLETED" },
];

const TransactionStepper: React.FC<{
  status: string | null;
  failedAtStep?: string | null;
  failureReason?: string | null;
}> = ({ status, failedAtStep, failureReason }) => {
  const { t } = useTranslation();
  const isFailure = status === "FAILED";

  // Two index calculations:
  //  - happy path: highlight the current step and mark prior as done.
  //    On the terminal step (`ON_CHAIN_COMPLETED`) we collapse "active"
  //    into "done" so the last cell stops pulsing as in-flight and
  //    flips to ✓ — the chain is finished, nothing more is happening.
  //  - failure: the chain broke RIGHT AFTER `failedAtStep`. Steps up
  //    to and including failedAtStep stay green ✓ (they happened);
  //    the next cell wears the X; everything after stays gray.
  const currentIdx = Math.max(
    0,
    TX_STEPS.findIndex((s) => s.key === status)
  );
  const failedIdx = failedAtStep ? TX_STEPS.findIndex((s) => s.key === failedAtStep) + 1 : 0;
  const isTerminalSuccess = !isFailure && status === TX_STEPS[TX_STEPS.length - 1].key;

  return (
    <>
      <ol className={`alfred-stepper ${isFailure ? "alfred-stepper--has-failure" : ""}`}>
        {TX_STEPS.map((step, i) => {
          const done = isFailure
            ? i < failedIdx
            : isTerminalSuccess
              ? i <= currentIdx
              : i < currentIdx;
          const active = !isFailure && !isTerminalSuccess && i === currentIdx;
          const failed = isFailure && i === failedIdx;
          return (
            <li
              key={step.key}
              className={[
                "alfred-stepper__step",
                done && "alfred-stepper__step--done",
                active && "alfred-stepper__step--active",
                failed && "alfred-stepper__step--failed",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="alfred-stepper__dot" aria-hidden>
                {failed ? "✕" : done ? "✓" : i + 1}
              </span>
              <span className="alfred-stepper__label">{t(step.labelKey)}</span>
            </li>
          );
        })}
      </ol>
      {isFailure && failureReason && (
        <p className="alfred-failure-reason">
          <span className="alfred-failure-reason__label">{t("DEPOSIT_FORM.FAILURE_REASON_LABEL")}</span>
          <span className="alfred-failure-reason__text">{failureReason}</span>
        </p>
      )}
    </>
  );
};

const ExpiryModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="alfred-modal-backdrop" role="dialog" aria-modal>
      <div className="alfred-modal">
        <h2 className="alfred-modal__title">{t("DEPOSIT_FORM.QUOTE_EXPIRED_TITLE")}</h2>
        <p className="alfred-modal__text">{t("DEPOSIT_FORM.QUOTE_EXPIRED_TEXT")}</p>
        <button className="alfred-modal__btn" onClick={onClose}>
          {t("DEPOSIT_FORM.QUOTE_EXPIRED_CTA")}
        </button>
      </div>
    </div>
  );
};

/**
 * Fullscreen take-over shown once the deposit hits a terminal state.
 * Two visual variants — success (green ✓ + credited amount) and
 * failure (red ✕ + reason). The user has to dismiss this before they
 * can start another deposit, so they don't miss the outcome.
 */
const CompletionModal: React.FC<{
  variant: "success" | "failure";
  amount?: string;
  currency?: string;
  reason?: string | null;
  onClose: () => void;
}> = ({ variant, amount, currency, reason, onClose }) => {
  const { t } = useTranslation();
  const isSuccess = variant === "success";
  return (
    <div className="alfred-modal-backdrop" role="dialog" aria-modal>
      <div className={`alfred-modal alfred-modal--${variant}`}>
        <div className={`alfred-modal__icon alfred-modal__icon--${variant}`} aria-hidden>
          {isSuccess ? "✓" : "✕"}
        </div>
        <h2 className="alfred-modal__title">{t(isSuccess ? "DEPOSIT_FORM.COMPLETED_TITLE" : "DEPOSIT_FORM.FAILED_TITLE")}</h2>
        {isSuccess && amount && currency && (
          <p className="alfred-modal__amount">
            +{amount} {currency}
          </p>
        )}
        <p className="alfred-modal__text">
          {isSuccess ? t("DEPOSIT_FORM.COMPLETED_TEXT") : reason || t("DEPOSIT_FORM.FAILED_TEXT")}
        </p>
        <button className="alfred-modal__btn" onClick={onClose}>
          {t(isSuccess ? "DEPOSIT_FORM.COMPLETED_CTA" : "DEPOSIT_FORM.FAILED_CTA")}
        </button>
      </div>
    </div>
  );
};

/**
 * Compact dev-only panel for QA. Two buttons drive preset scenarios:
 * `success` walks the canonical ONRAMP chain server-side with pauses
 * (so polling sees progression), `failure` fires a single FAILED
 * event. Visible whenever the backend exposes the dev statuses
 * endpoint (i.e. on VAKOTEST / *UAT exchanges) — fetch is gated by a
 * 200 from `getDevStatuses`, so production stays silent.
 */
const DevSimulator: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState<"success" | "failure" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    alfredService
      .getDevStatuses()
      .then(() => {
        if (!cancelled) setAvailable(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const fire = async (scenario: "success" | "failure") => {
    setBusy(scenario);
    setError(null);
    try {
      await alfredService.devEmitOnramp({ session_id: sessionId, scenario });
    } catch (e) {
      setError(t("DEPOSIT_FORM.DEV_EMIT_FAILED"));
      console.error("Dev emit failed:", e);
    } finally {
      setBusy(null);
    }
  };

  if (!available) return null;
  return (
    <details className="alfred-dev-simulator">
      <summary className="alfred-dev-simulator__title">{t("DEPOSIT_FORM.DEV_SIMULATOR_TITLE")}</summary>
      <div className="alfred-dev-simulator__content">
        <button
          className="alfred-dev-simulator__btn alfred-dev-simulator__btn--success"
          onClick={() => fire("success")}
          disabled={busy !== null}
        >
          {busy === "success" ? t("DEPOSIT_FORM.DEV_EMIT_BUSY") : t("DEPOSIT_FORM.DEV_SIM_SUCCESS")}
        </button>
        <button
          className="alfred-dev-simulator__btn alfred-dev-simulator__btn--failure"
          onClick={() => fire("failure")}
          disabled={busy !== null}
        >
          {busy === "failure" ? t("DEPOSIT_FORM.DEV_EMIT_BUSY") : t("DEPOSIT_FORM.DEV_SIM_FAILURE")}
        </button>
        {error && <div className="alfred-dev-simulator__error">{error}</div>}
      </div>
    </details>
  );
};

/**
 * Renders the bank instructions Alfred returned on /onramp creation,
 * polls live transaction status, and surfaces an expiry modal when
 * the quote runs out.
 */
const AlfredPaymentInstructions: React.FC<Props> = ({
  sessionId,
  transaction,
  precision,
  netReceiveAmount,
  onReset,
}): JSX.Element => {
  // The received (stable) side is shown at the platform display precision —
  // the raw Alfred quote carries more decimals than the wallet renders.
  const formatStable = (raw: string | undefined | null): string => {
    const n = Number(raw);
    return Number.isFinite(n) ? formatNumberToLocale(n, precision) : formatAmount(raw);
  };
  // What the user actually gets in their wallet: net of the platform fee.
  // Falls back to the gross `toAmount` when the net isn't available.
  const receivedDisplay =
    netReceiveAmount && netReceiveAmount > 0
      ? formatNumberToLocale(netReceiveAmount, precision)
      : formatStable(transaction.toAmount);
  const { t } = useTranslation();
  const instructions: AlfredFiatPaymentInstructions | undefined = transaction.fiatPaymentInstructions;
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const snapshot = useAlfredSessionPolling(sessionId, pollingEnabled);
  const status = snapshot?.transactionStatus ?? transaction.status ?? null;

  // Apply the host-app language preference forwarded by the backend
  // (set on first session response). Without this the iframe falls
  // back to the URL `?lang=` param, which not every embedder passes.
  useEffect(() => {
    const lang = snapshot?.language;
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => undefined);
    }
  }, [snapshot?.language]);

  // The countdown should track the **quote** validity (~1h, what the
  // user is locked in at), not the bank-deposit expiration. They tend
  // to be within seconds of each other on Alfred's response, but the
  // quote is the right semantic anchor — once it expires the rate is
  // void and we have to re-quote.
  const expiry =
    transaction.quote?.expiration ??
    transaction.expirationDate ??
    instructions?.expirationDate;
  const { remaining, expired } = useCountdown(expiry);
  const [acknowledgedExpiry, setAcknowledgedExpiry] = useState(false);
  // Acknowledgement is per-session — once the user dismisses the
  // completion modal we don't pop it back up (state is reset on the
  // next deposit because the parent re-mounts this component on
  // `resetDeposit`). Must live BEFORE any early return so hook order
  // stays stable across renders.
  const [acknowledgedCompletion, setAcknowledgedCompletion] = useState(false);

  // Stop polling on expiry — until the user resets we don't need
  // fresh data, and it'd just hammer the backend.
  useEffect(() => {
    if (expired) setPollingEnabled(false);
  }, [expired]);

  if (!instructions) {
    return (
      <section className="alfred-instructions">
        <h2 className="alfred-instructions__title">{t("DEPOSIT_FORM.PAYMENT_INSTRUCTIONS_UNAVAILABLE")}</h2>
      </section>
    );
  }

  const allEntries = Object.entries(instructions).filter(
    ([key, value]) => !HEADER_KEYS.has(key) && value != null && value !== ""
  );
  // CO/COP route hosts the PSE redirect inside an iframe instead of
  // surfacing the URL as a copy-row. Other rails keep the text-row
  // behavior (or get sent through the standalone PaymentRedirect page
  // upstream in useDepositActions).
  const redirectUrl = (instructions as { redirectUrl?: string }).redirectUrl;
  const showPseIframe =
    transaction.fromCurrency === "COP" && typeof redirectUrl === "string" && redirectUrl.length > 0;
  // CO/COP (PSE) returns `paymentType: null` — it's a hosted redirect, not a
  // named rail. Guard the title so it doesn't crash the whole screen on
  // `.replace()` of null (which blanked the Colombia deposit, hiding the
  // amount block + the PSE iframe).
  const railLabel = instructions.paymentType
    ? instructions.paymentType.replace(/_/g, " ")
    : showPseIframe
      ? "PSE"
      : transaction.fromCurrency;
  // `paymentDescription` is prose, not actionable bank data — strip
  // it out of the credentials list and surface it as a small note
  // below so it doesn't compete with the values the user copies.
  // `HIDDEN_KEYS` covers QR-redundant fields (`qrCode`, `paymentLink`,
  // `deepLink`, `qrCodeImage`) — those are already encoded in the QR
  // image at the top. `pixCode` stays in body as a copyable
  // "PIX NUMBER" row because the user often pastes it directly.
  const qrEntry = allEntries.find(([key]) => QR_KEYS.has(key));
  const qrValue = qrEntry ? formatValue(qrEntry[1]) : null;
  const bodyEntries = allEntries.filter(
    ([key]) =>
      key !== "paymentDescription" &&
      !HIDDEN_KEYS.has(key) &&
      !(showPseIframe && key === "redirectUrl")
  );
  const description = (instructions as { paymentDescription?: string }).paymentDescription;

  const handleExpiryClose = () => {
    setAcknowledgedExpiry(true);
    onReset();
  };

  const isTerminal = status != null && TERMINAL_STATUSES.has(status);
  const completionVariant: "success" | "failure" | null = !isTerminal ? null : status === "FAILED" ? "failure" : "success";
  const showCompletionModal = completionVariant !== null && !acknowledgedCompletion;

  const handleCompletionClose = () => {
    setAcknowledgedCompletion(true);
    onReset();
  };

  const showExpiryModal = expired && !acknowledgedExpiry && !isTerminal;

  return (
    <>
      <section className="alfred-instructions">
        <header className="alfred-instructions__header">
          <h2 className="alfred-instructions__title">{t("DEPOSIT_FORM.PAY_VIA", { rail: railLabel })}</h2>
          {expiry && (
            <div className={`alfred-instructions__countdown ${expired ? "alfred-instructions__countdown--expired" : ""}`}>
              {expired ? t("DEPOSIT_FORM.QUOTE_EXPIRED") : t("DEPOSIT_FORM.EXPIRES_IN", { time: remaining })}
            </div>
          )}
        </header>

        {transaction.fromAmount && transaction.toAmount && (
          <div className="alfred-instructions__amount-block">
            <Row
              label={t("DEPOSIT_FORM.AMOUNT_SEND")}
              value={`${formatAmount(transaction.fromAmount)} ${transaction.fromCurrency}`}
              copyable
            />
            <Row
              label={t("DEPOSIT_FORM.AMOUNT_RECEIVE")}
              value={`${receivedDisplay} ${transaction.toCurrency}`}
              copyable
            />
          </div>
        )}

        {qrValue && (
          <div className="alfred-instructions__qr-wrap">
            {/* White card sized to the QR exactly (no flex children that
                would stretch it taller than wide). The rail hint sits
                below the card so the card itself stays a tight square. */}
            <div className="alfred-instructions__qr">
              <QRCodeSVG value={qrValue} size={180} marginSize={0} level="M" />
            </div>
            <span className="alfred-instructions__qr-hint">
              {t("DEPOSIT_FORM.QR_HINT", { rail: railLabel })}
            </span>
          </div>
        )}

        <div className="alfred-instructions__body">
          {bodyEntries.map(([key, value]) => {
            const labelKey = INSTRUCTION_LABEL_KEY[key];
            const label = labelKey
              ? t(`ALFRED.INSTRUCTION.${labelKey}`, { defaultValue: LABELS[key] ?? humaniseKey(key) })
              : humaniseKey(key);
            return <Row key={key} label={label} value={formatValue(value)} copyable />;
          })}
        </div>

        {showPseIframe && (
          <>
            <div className="alfred-instructions__pse-disclaimer" role="alert">
              <span className="alfred-instructions__pse-disclaimer-icon" aria-hidden>
                ⚠️
              </span>
              <p className="alfred-instructions__pse-disclaimer-text">
                {t("DEPOSIT_FORM.PSE_AMOUNT_DISCLAIMER", {
                  amount: formatAmount(transaction.fromAmount),
                  currency: transaction.fromCurrency,
                  defaultValue:
                    "Important: Coink's payment form may let you edit the amount, but your deposit will only match the locked quote if you enter exactly {{amount}} {{currency}}. Entering any other value will cause the deposit to fail or be rejected as a quote mismatch.",
                })}
              </p>
            </div>
            {/* Coink's PSE form is built for a full page — its inputs/headings
                render oversized in our narrow embed. We can't restyle a
                cross-origin iframe, so synthetically zoom the whole frame out
                with a CSS transform (the wrapper clips the up-scaled size). */}
            <div className="alfred-instructions__pse-frame">
              <iframe
                className="alfred-instructions__pse-iframe"
                src={redirectUrl}
                title={t("DEPOSIT_FORM.PSE_IFRAME_TITLE", { defaultValue: "PSE payment" })}
                allow="payment *"
              />
            </div>
          </>
        )}

        {description && <p className="alfred-instructions__note">{description}</p>}

        {/* Stepper-then-banner: the dotted progress is the eye anchor
            the user scans first ("where am I in the chain?"); the
            text banner restates the same thing for accessibility +
            failure-reason details below it. */}
        <TransactionStepper
          status={status}
          failedAtStep={snapshot?.failedAtStep ?? null}
          failureReason={snapshot?.failureReason ?? null}
        />
        <StatusBanner status={status} />

        <DevSimulator sessionId={sessionId} />
      </section>

      {showExpiryModal && <ExpiryModal onClose={handleExpiryClose} />}
      {showCompletionModal && completionVariant && (
        <CompletionModal
          variant={completionVariant}
          amount={completionVariant === "success" ? receivedDisplay : undefined}
          currency={completionVariant === "success" ? transaction.toCurrency : undefined}
          reason={snapshot?.failureReason ?? null}
          onClose={handleCompletionClose}
        />
      )}
    </>
  );
};

export default AlfredPaymentInstructions;
